import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '@crm/shared';
import { toast } from 'react-toastify';
import { fmtDate } from '../utils/dateUtils';
import {
  FiBarChart2, FiRefreshCw, FiDownload, FiSearch,
  FiAlertCircle, FiCalendar, FiDollarSign,
  FiFileText, FiCheckCircle,
  FiTrendingUp, FiUsers, FiActivity, FiFilter, FiX,
  FiChevronLeft, FiChevronRight, FiPhone, FiTarget,
} from 'react-icons/fi';

/* ─── Design Tokens (matches rest of app) ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyM:'#1A5276', navyL:'#EBF4FF',
  gold:'#E8930A', goldL:'#FEF3DC', goldB:'#F5A623',
  green:'#15803D', greenL:'#DCFCE7', greenB:'#16A34A',
  sky:'#0369A1', skyL:'#DBEAFE', skyB:'#0284C7',
  purple:'#6D28D9', purpleL:'#EDE9FE', purpleB:'#7C3AED',
  teal:'#0D9488', tealL:'#CCFBF1', tealB:'#5EEAD4',
  red:'#B91C1C', redL:'#FEF2F2', redB:'#DC2626', redBord:'#FECACA',
  border:'#E5E7EB', borderL:'#F3F4F6',
  bg:'#F4F6F9', white:'#FFFFFF',
  t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Helpers ─── */
const fmtAmt  = n  => (n != null && n !== '' && n !== 0) ? '₹' + Number(n).toLocaleString('en-IN') : '₹0';
const PAGE_SIZE = 20;

const now    = new Date();
const lmDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
const lmName = lmDate.toLocaleString('en-IN', { month:'long', year:'numeric' });

/* ─── Current month default ─── */
const curYear  = now.getFullYear();
const curMonth = now.getMonth() + 1; // 1-based

/* ─── Shimmer ─── */
const Sh = ({ h=20, w='100%', r=6, mb=0 }) => (
  <div style={{ height:h, width:w, borderRadius:r, marginBottom:mb,
    background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
    backgroundSize:'400px 100%', animation:'rptShimmer 1.4s infinite' }}/>
);

/* ─── Pill ─── */
const Pill = ({ label, bg, color, border }) => (
  <span style={{ display:'inline-flex', alignItems:'center', padding:'2px 10px', borderRadius:999,
    fontSize:11, fontWeight:700, background:bg, color, border:`1px solid ${border||bg}`, whiteSpace:'nowrap' }}>
    {label}
  </span>
);

/* ─── KPI Card ─── */
const Kpi = ({ label, value, icon, iconBg, iconColor, accent, loading, sub, onClick }) => (
  <div onClick={onClick} style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
    padding:'16px 18px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)', position:'relative', overflow:'hidden',
    cursor: onClick ? 'pointer' : 'default' }}>
    <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:accent, borderRadius:'12px 12px 0 0' }}/>
    <div style={{ width:36, height:36, borderRadius:9, background:iconBg,
      display:'flex', alignItems:'center', justifyContent:'center', color:iconColor, marginBottom:10 }}>{icon}</div>
    {loading ? <Sh h={28} w="50%" mb={6}/> :
      <div style={{ fontSize:24, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1, marginBottom:5 }}>
        {value ?? '—'}
      </div>}
    <div style={{ fontSize:12, fontWeight:600, color:C.t3 }}>{label}</div>
    {sub && !loading && <div style={{ fontSize:11, color:C.t4, marginTop:3 }}>{sub}</div>}
  </div>
);

/* ─── Error state ─── */
const Err = ({ msg, onRetry }) => (
  <div style={{ padding:'32px 24px', textAlign:'center' }}>
    <div style={{ background:C.redL, border:`1px solid ${C.redBord}`, borderRadius:12,
      padding:'18px 22px', display:'inline-block', maxWidth:480, textAlign:'left' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <FiAlertCircle size={15} style={{ color:C.redB }}/>
        <span style={{ fontWeight:700, color:C.redB, fontSize:13 }}>Could not load data</span>
      </div>
      <p style={{ color:'#7F1D1D', fontSize:12.5, margin:'0 0 12px' }}>{msg}</p>
      {onRetry && (
        <button onClick={onRetry}
          style={{ background:C.redB, color:C.white, border:'none', borderRadius:8,
            padding:'7px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
          Retry
        </button>
      )}
    </div>
  </div>
);

/* ─── Empty state ─── */
const Empty = ({ label = 'No data available' }) => (
  <div style={{ textAlign:'center', padding:'56px 24px' }}>
    <div style={{ width:52, height:52, borderRadius:'50%', background:C.navyL,
      display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
      <FiCalendar size={22} style={{ color:C.navy, opacity:0.4 }}/>
    </div>
    <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>{label}</div>
    <div style={{ fontSize:12.5, color:C.t3 }}>Data will appear here once available</div>
  </div>
);

/* ─── Section Card ─── */
const Card = ({ title, icon, badge, children, headerRight }) => (
  <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
    overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
      padding:'13px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        {icon && <span style={{ color:C.navy, opacity:0.65, display:'flex' }}>{icon}</span>}
        <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>{title}</span>
        {badge != null && (
          <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:999,
            fontSize:10.5, fontWeight:700, border:`1px solid rgba(13,59,94,0.12)` }}>{badge}</span>
        )}
      </div>
      {headerRight}
    </div>
    <div style={{ padding:18 }}>{children}</div>
  </div>
);

/* ─── CSV export ─── */
const exportCsv = (headers, rows, filename) => {
  const csv = [headers, ...rows]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(','))
    .join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([csv], { type:'text/csv' })),
    download: `${filename}_${new Date().toISOString().slice(0,10)}.csv`,
  });
  a.click(); URL.revokeObjectURL(a.href);
};

/* ─── Pipeline bar ─── */
const PBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <span style={{ fontSize:12.5, fontWeight:500, color:C.t2 }}>{label}</span>
        <div style={{ display:'flex', gap:8 }}>
          <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>{value}</span>
          <span style={{ fontSize:11, color:C.t4, minWidth:30, textAlign:'right' }}>{pct}%</span>
        </div>
      </div>
      <div style={{ height:6, background:'#F1F5F9', borderRadius:999, overflow:'hidden' }}>
        <div style={{ height:'100%', background:color, borderRadius:999, width:`${pct}%`,
          minWidth: value > 0 ? 4 : 0, transition:'width 0.7s ease-out' }}/>
      </div>
    </div>
  );
};

/* ═════════════════════════════════════════════════════════════════
   TAB 1 — LAST MONTH MIS
   Real API: GET /api/leads/mis/last_month/
   Response: { "last_month_cases": 0 }
   (Returns a summary count, not a paginated list.)
═════════════════════════════════════════════════════════════════ */
function LastMonthMIS() {
  const [data, setData]       = useState(null);   // { last_month_cases: number }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getMisLastMonth();
      const d = res?.data;
      setData(d);
    } catch (e) {
      setError(e?.message || e?.detail || String(e) || 'Failed to load last month MIS');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cases = data?.last_month_cases ?? 0;

  const handleExport = async () => {
    setExporting(true);
    try {
      const allLeads = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await api.getLeads({ page, page_size: 100 });
        const d = res?.data;
        const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
        allLeads.push(...results);
        hasMore = !!d?.next && results.length > 0;
        page++;
      }

      const lmStart = new Date(lmDate.getFullYear(), lmDate.getMonth(), 1);
      const lmEnd = new Date(lmDate.getFullYear(), lmDate.getMonth() + 1, 0, 23, 59, 59);

      const monthLeads = allLeads.filter(l => {
        const d = l.disbursement_date || l.login_date || l.created_at;
        if (!d) return false;
        const dt = new Date(d);
        return dt >= lmStart && dt <= lmEnd;
      });

      const stageMap = {
        FOLLOW_UP: 'Follow-up', LOGIN: 'Login', SANCTION: 'Sanction',
        DISBURSEMENT: 'Disbursement', PDD: 'PDD', REVENUE: 'Revenue', CLOSED: 'Closed',
      };

      exportCsv(
        ['Lead No','Customer','Mobile','Product','Lead Source','BSA Code','Institution','Channel','Banker','Stage','Login Date','Sanction Date','Expected Disb Date','Disbursement Date','Disbursed Amount','Sanctioned Amount','Payout to Company','Revenue Received','Invoice No','Executive','Remarks'],
        monthLeads.map(l => {
          const exec = [l.created_by_detail?.first_name, l.created_by_detail?.last_name].filter(Boolean).join(' ')
            || l.created_by_name || '—';
          return [
            l.lead_no || `#${l.id}`,
            l.customer_name || '',
            l.mobile_number || '',
            l.product?.name || '',
            l.lead_source || '',
            l.bsa?.code || '',
            l.institution?.name || '',
            l.channel?.name || '',
            l.banker_name || '',
            stageMap[l.current_stage] || l.status?.name || '',
            l.login_date || '',
            l.sanction_date || '',
            l.expected_disbursement_date || '',
            l.disbursement_date || '',
            l.disbursed_amount || '',
            l.sanctioned_amount || '',
            l.payout_to_company || '',
            l.revenue_received || '',
            l.invoice_number || '',
            exec,
            l.remarks || '',
          ];
        }),
        `last_month_mis_${lmDate.getFullYear()}_${lmDate.getMonth()+1}`
      );
    } catch (e) {
      toast.error('Failed to export last month data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div style={{ background:C.skyL, border:`1px solid #BAE6FD`, borderRadius:10,
        padding:'10px 16px', display:'flex', alignItems:'flex-start', gap:9, marginBottom:14 }}>
        <FiFileText size={13} style={{ color:C.sky, marginTop:1, flexShrink:0 }}/>
        <span style={{ fontSize:12.5, color:C.sky, fontWeight:500, lineHeight:1.55 }}>
          <strong>Last Month MIS:</strong> Total disbursed/processed cases from <strong>{lmName}</strong>.
          This is a read-only monthly summary.
        </span>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
        <Kpi label="Total Cases Last Month" value={loading ? null : cases}
          icon={<FiFileText size={15}/>} iconBg={C.navyL} iconColor={C.navy}
          accent={C.navy} loading={loading} sub={lmName}/>
        <Kpi label="Month" value={loading ? null : lmDate.toLocaleString('en-IN',{month:'short'})}
          icon={<FiCalendar size={15}/>} iconBg={C.skyL} iconColor={C.skyB}
          accent={C.skyB} loading={loading} sub={String(lmDate.getFullYear())}/>
        <Kpi label="Status" value={loading ? null : (cases > 0 ? 'Data Available' : 'No Cases')}
          icon={<FiCheckCircle size={15}/>} iconBg={cases > 0 ? C.greenL : C.borderL}
          iconColor={cases > 0 ? C.greenB : C.t3}
          accent={cases > 0 ? C.greenB : '#94A3B8'} loading={loading}/>
      </div>

      <Card title={`Last Month Summary — ${lmName}`} icon={<FiBarChart2 size={13}/>}
        headerRight={
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={load}
              style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                background:C.white, color:C.t3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <FiRefreshCw size={13}/>
            </button>
            <button onClick={handleExport} disabled={exporting}
              style={{ height:32, padding:'0 14px', borderRadius:7, background:exporting ? C.border : C.goldB,
                color:exporting ? C.t4 : '#0D1B2A', border:'none', fontSize:12, fontWeight:700,
                cursor:exporting ? 'not-allowed' : 'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', gap:5 }}>
              {exporting
                ? <><div style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(13,27,42,0.3)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} /> Exporting…</>
                : <><FiDownload size={11}/> Export CSV</>}
            </button>
          </div>
        }>
        {loading && <div style={{ display:'flex', flexDirection:'column', gap:10 }}>{[0,1,2].map(i=><Sh key={i} h={40}/>)}</div>}
        {!loading && error && <Err msg={error} onRetry={load}/>}
        {!loading && !error && (
          <div>
            {/* Summary row */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <div style={{ background:C.navyL, borderRadius:10, padding:'16px 18px', borderTop:`3px solid ${C.navy}` }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 }}>Total Cases</div>
                <div style={{ fontSize:28, fontWeight:800, color:C.t1, letterSpacing:'-0.04em' }}>{cases}</div>
                <div style={{ fontSize:11.5, color:C.t3, marginTop:4 }}>{lmName}</div>
              </div>
              <div style={{ background:cases > 0 ? C.greenL : C.borderL, borderRadius:10, padding:'16px 18px',
                borderTop:`3px solid ${cases > 0 ? C.greenB : '#94A3B8'}` }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 }}>Summary</div>
                <div style={{ fontSize:14, fontWeight:700, color:cases > 0 ? C.greenB : C.t3 }}>
                  {cases > 0 ? `${cases} case${cases !== 1 ? 's' : ''} processed` : 'No cases recorded'}
                </div>
                <div style={{ fontSize:11.5, color:C.t3, marginTop:4 }}>for {lmName}</div>
              </div>
            </div>

            {/* Note about API */}
            <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:9, padding:'12px 14px',
              fontSize:12.5, color:'#92400E', lineHeight:1.55 }}>
              <strong>ℹ API Note:</strong> The <code>/api/leads/mis/last_month/</code> endpoint currently returns a case count summary.
              Detailed case breakdown will be available when the backend exposes individual records.
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   TAB 2 — OVERALL MIS
   Real API: GET /api/leads/mis/overall/?year=YYYY&month=MM
   Response: { "detail": "year and month required" } if missing params
   Once params supplied → actual response shape TBD; handled gracefully.
═════════════════════════════════════════════════════════════════ */
function OverallMIS() {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [year,  setYear]      = useState(String(curYear));
  const [month, setMonth]     = useState(String(curMonth));
  const [searched, setSearched] = useState(false);

  const load = useCallback(async (y = year, m = month) => {
    if (!y || !m) { setError('Please select both year and month.'); return; }
    setLoading(true); setError(null); setSearched(true);
    try {
      // API requires year and month as query params
      const res = await api.getMisOverall({ year: y, month: Number(m) });
      const d   = res?.data;
      // Handle backend error object gracefully
      if (d?.detail) throw new Error(d.detail);
      setData(d);
    } catch (e) {
      setError(e?.message || e?.detail || String(e) || 'Failed to load overall MIS');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  // Derive display values — API returns flat summary object:
  // { total_leads, login_stage, sanction_stage, disbursement_stage,
  //   pdd_stage, revenue_stage, closed_stage,
  //   total_disbursement, total_revenue_received }
  const rows      = Array.isArray(data?.results) ? data.results
                  : Array.isArray(data?.data)    ? data.data
                  : Array.isArray(data)           ? data
                  : [];

  // Flat summary fields (API returns user-wise aggregated data in results array)
  const totalCount  = rows.reduce((s,r) => s + Number(r.total_leads||0), 0);
  const totalDisb   = rows.reduce((s,r) => s + Number(r.total_disbursement||0), 0);
  const totalRev    = rows.reduce((s,r) => s + Number(r.total_revenue||0), 0);

  // Stage-wise counts from rows (user-wise aggregated)
  const loginStage  = rows.reduce((s,r) => s + Number(r.login_stage||0), 0);
  const sancStage   = rows.reduce((s,r) => s + Number(r.sanction_stage||0), 0);
  const disbStage   = rows.reduce((s,r) => s + Number(r.disbursement_stage||0), 0);
  const pddStage    = rows.reduce((s,r) => s + Number(r.pdd_stage||0), 0);
  const revStage    = rows.reduce((s,r) => s + Number(r.revenue_stage||0), 0);
  const closedStage = rows.reduce((s,r) => s + Number(r.closed_stage||0), 0);

  const monthLabel = year && month
    ? new Date(`${year}-${month}-01`).toLocaleString('en-IN', { month:'long', year:'numeric' })
    : '';

  const YEARS  = Array.from({ length: 5 }, (_, i) => String(curYear - i));
  const MONTHS = [
    { v:'1', l:'January'   }, { v:'2', l:'February'  }, { v:'3', l:'March'     },
    { v:'4', l:'April'     }, { v:'5', l:'May'        }, { v:'6', l:'June'      },
    { v:'7', l:'July'      }, { v:'8', l:'August'     }, { v:'9', l:'September' },
    { v:'10',l:'October'   }, { v:'11',l:'November'   }, { v:'12',l:'December'  },
  ];

  const doExport = () => {
    if (rows.length > 0) {
      exportCsv(
        ['#','Executive','Total Leads','Login','Sanction','Disbursement','PDD','Revenue','Closed','Disbursed Amount','Revenue Amount'],
        rows.map((r, i) => {
          const exec = [r.internal_staff__first_name, r.internal_staff__last_name].filter(Boolean).join(' ') || '—';
          return [
            i + 1,
            exec,
            r.total_leads || 0,
            r.login_stage || 0,
            r.sanction_stage || 0,
            r.disbursement_stage || 0,
            r.pdd_stage || 0,
            r.revenue_stage || 0,
            r.closed_stage || 0,
            r.total_disbursement || '0.00',
            r.total_revenue || '0.00',
          ];
        }),
        `overall_mis_${year}_${month}`
      );
    }
  };

  return (
    <>
      {/* Param selector */}
      <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
        padding:'16px 20px', marginBottom:14, boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
        <div style={{ fontSize:11.5, fontWeight:700, color:C.t3, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
          Select Period
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Year</label>
            <select value={year} onChange={e => setYear(e.target.value)}
              style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
                fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit', minWidth:100 }}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Month</label>
            <select value={month} onChange={e => setMonth(e.target.value)}
              style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
                fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit', minWidth:140 }}>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <button onClick={() => load(year, month)}
            style={{ height:36, padding:'0 20px', borderRadius:8, background:C.navy, color:'#fff',
              border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
              display:'flex', alignItems:'center', gap:6 }}>
            <FiFilter size={12}/> Load Report
          </button>
          {rows.length > 0 && (
            <button onClick={doExport}
              style={{ height:36, padding:'0 16px', borderRadius:8, background:C.goldB, color:'#0D1B2A',
                border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', gap:6 }}>
              <FiDownload size={12}/> Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Not yet searched */}
      {!searched && !loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'48px 24px', textAlign:'center', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ width:52, height:52, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
            <FiBarChart2 size={22} style={{ color:C.navy, opacity:0.5 }}/>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>Select a period to load the Overall MIS</div>
          <div style={{ fontSize:12.5, color:C.t3 }}>Choose year and month, then click "Load Report"</div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[0,1,2,3,4].map(i => <Sh key={i} h={48}/>)}
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && <Err msg={error} onRetry={() => load(year, month)}/>}

      {/* Results */}
      {!loading && !error && searched && data !== null && (
        <>
          {/* KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            <Kpi label="Total Leads"         value={totalCount}        icon={<FiFileText size={15}/>}    iconBg={C.navyL}  iconColor={C.navy}  accent={C.navy}   loading={false} sub={monthLabel}/>
            <Kpi label="Total Disbursement"  value={fmtAmt(totalDisb)} icon={<FiDollarSign size={15}/>}  iconBg={C.greenL} iconColor={C.greenB} accent={C.greenB} loading={false}/>
            <Kpi label="Total Revenue"       value={fmtAmt(totalRev)}  icon={<FiTrendingUp size={15}/>}  iconBg={C.tealL}  iconColor={C.tealB} accent={C.tealB}  loading={false}/>
            <Kpi label="Closed Leads"        value={closedStage}       icon={<FiCheckCircle size={15}/>} iconBg="#F1F5F9"  iconColor="#64748B" accent="#94A3B8"  loading={false}/>
          </div>

          {/* Summary display — API returns flat aggregate object */}
          {rows.length === 0 && (
            <>
              {totalCount === 0 ? (
                <Card title={`Overall MIS — ${monthLabel}`} icon={<FiBarChart2 size={13}/>}>
                  <Empty label={`No records found for ${monthLabel}`}/>
                </Card>
              ) : (
                <>
                  {/* Stage-wise pipeline breakdown */}
                  <Card title={`Stage-wise Breakdown — ${monthLabel}`} icon={<FiBarChart2 size={13}/>}
                    headerRight={
                      <button onClick={() => load(year, month)}
                        style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                          background:C.white, color:C.t3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <FiRefreshCw size={13}/>
                      </button>
                    }>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
                      {[
                        { label:'Login',        value:loginStage,  color:C.purple, bg:C.purpleL, stage:'LOGIN' },
                        { label:'Sanction',     value:sancStage,   color:C.skyB,   bg:C.skyL,    stage:'SANCTION' },
                        { label:'Disbursement', value:disbStage,   color:C.greenB, bg:C.greenL,  stage:'DISBURSEMENT' },
                        { label:'PDD',          value:pddStage,    color:C.navy,   bg:C.navyL,   stage:'PDD' },
                        { label:'Revenue',      value:revStage,    color:C.tealB,  bg:C.tealL,   stage:'REVENUE' },
                        { label:'Closed',       value:closedStage, color:'#64748B',bg:'#F1F5F9', stage:'CLOSED' },
                      ].map(s => (
                        <div key={s.label} onClick={() => navigate(`/leads?stage=${s.stage}`)} style={{ background:s.bg, borderRadius:10, padding:'14px 16px',
                          borderTop:`3px solid ${s.color}`, cursor:'pointer', transition:'transform 0.15s,box-shadow 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 16px rgba(13,59,94,0.12)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                          <div style={{ fontSize:10, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>{s.label}</div>
                          <div style={{ fontSize:26, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1 }}>{s.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Pipeline progress bars */}
                    <div style={{ borderTop:`1px solid ${C.borderL}`, paddingTop:14 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>Distribution</div>
                      {[
                        { label:'Login',        value:loginStage,  color:C.purple },
                        { label:'Sanction',     value:sancStage,   color:C.skyB   },
                        { label:'Disbursement', value:disbStage,   color:C.greenB },
                        { label:'PDD',          value:pddStage,    color:C.navy   },
                        { label:'Revenue',      value:revStage,    color:C.tealB  },
                        { label:'Closed',       value:closedStage, color:'#94A3B8'},
                      ].map(s => <PBar key={s.label} label={s.label} value={s.value} total={totalCount} color={s.color}/>)}
                    </div>
                  </Card>
                </>
              )}
            </>
          )}

          {/* Table when rows available */}
          {rows.length > 0 && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
              overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
              <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC',
                display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <FiBarChart2 size={14} style={{ color:C.navy, opacity:0.7 }}/>
                  <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>Overall MIS — {monthLabel}</span>
                  <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:999,
                    fontSize:10.5, fontWeight:700, border:`1px solid rgba(13,59,94,0.12)` }}>{rows.length}</span>
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {['#','Executive','Leads','Login','Sanction','Disbursement','PDD','Revenue','Closed','Disb. Amount','Revenue Amount'].map((h,i)=>(
                        <th key={h} style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t2,
                          textTransform:'uppercase', letterSpacing:'0.07em',
                          textAlign:(i>=9)?'right':'left',
                          borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const isLast = idx === rows.length - 1;
                      const exec   = [r.internal_staff__first_name, r.internal_staff__last_name].filter(Boolean).join(' ') || '—';
                      const execInitials = exec.split(' ').map(p=>p[0]).slice(0,2).join('').toUpperCase()||'?';
                      return (
                        <tr key={r.internal_staff||idx}>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t4 }}>{idx+1}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <div style={{ width:24, height:24, borderRadius:'50%', background:C.navyL,
                                display:'flex', alignItems:'center', justifyContent:'center', color:C.navy, fontSize:9, fontWeight:800 }}>
                                {execInitials}
                              </div>
                              <span style={{ fontSize:12.5, fontWeight:600, color:C.t1 }}>{exec}</span>
                            </div>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontWeight:700, fontSize:13, color:C.t1 }}>{r.total_leads||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.login_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.sanction_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.disbursement_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.pdd_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.revenue_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>{r.closed_stage||0}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:800, color:C.greenB }}>{fmtAmt(r.total_disbursement)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:700, color:C.tealB }}>{fmtAmt(r.total_revenue)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   TAB 3 — PIPELINE MIS
   Real API: GET /api/leads/mis/pipeline/
   Response: { follow_up:4, login:2, sanction:0, disbursement:0, pdd:2, revenue:0, closed:1 }
   This is a SUMMARY object, not a list — rendered as visual pipeline.
═════════════════════════════════════════════════════════════════ */
function PipelineMIS() {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getMisPipeline();
      const d   = res?.data;
      if (d?.detail) throw new Error(d.detail);
      setData(d);
    } catch (e) {
      setError(e?.message || e?.detail || String(e) || 'Failed to load pipeline MIS');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Real API keys: follow_up, login, sanction, disbursement, pdd, revenue, closed
  const fu    = data?.follow_up    ?? 0;
  const login = data?.login        ?? 0;
  const sanc  = data?.sanction     ?? 0;
  const disb  = data?.disbursement ?? 0;
  const pdd   = data?.pdd          ?? 0;
  const rev   = data?.revenue      ?? 0;
  const closed= data?.closed       ?? 0;
  const total = fu + login + sanc + disb + pdd + rev + closed;
  const active= fu + login + sanc + disb + pdd + rev; // excluding closed

  const stageCards = [
    { label:'Follow-up',   value:fu,    color:C.goldB,  bg:C.goldL,   icon:<FiPhone size={13}/>,      pct: total > 0 ? Math.round(fu/total*100)     : 0, stage:'FOLLOW_UP' },
    { label:'Login',       value:login, color:C.purple, bg:C.purpleL, icon:<FiFileText size={13}/>,   pct: total > 0 ? Math.round(login/total*100)  : 0, stage:'LOGIN' },
    { label:'Sanction',    value:sanc,  color:C.skyB,   bg:C.skyL,    icon:<FiCheckCircle size={13}/>,pct: total > 0 ? Math.round(sanc/total*100)   : 0, stage:'SANCTION' },
    { label:'PDD',         value:pdd,   color:C.navy,   bg:C.navyL,   icon:<FiTarget size={13}/>,     pct: total > 0 ? Math.round(pdd/total*100)    : 0, stage:'PDD' },
    { label:'Revenue',     value:rev,   color:C.tealB,  bg:C.tealL,   icon:<FiTrendingUp size={13}/>, pct: total > 0 ? Math.round(rev/total*100)    : 0, stage:'REVENUE' },
  ];

  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const allLeads = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await api.getLeads({ page, page_size: 100 });
        const d = res?.data;
        const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
        allLeads.push(...results);
        hasMore = !!d?.next && results.length > 0;
        page++;
      }

      const stageMap = {
        FOLLOW_UP: 'Follow-up',
        LOGIN: 'Login',
        SANCTION: 'Sanction',
        DISBURSEMENT: 'Disbursement',
        PDD: 'PDD',
        REVENUE: 'Revenue',
        CLOSED: 'Closed',
      };

      exportCsv(
        ['Lead No','Customer','Mobile','Product','Institution','Stage','Login Date','Sanction Date','Expected Disb Date','Sanctioned Amount','Executive'],
        allLeads.map(l => {
          const exec = [l.created_by_detail?.first_name, l.created_by_detail?.last_name].filter(Boolean).join(' ')
            || l.created_by_name || '—';
          return [
            l.lead_no || `#${l.id}`,
            l.customer_name || '',
            l.mobile_number || '',
            l.product?.name || '',
            l.institution?.name || '',
            stageMap[l.current_stage] || l.status?.name || '',
            l.login_date || '',
            l.sanction_date || '',
            l.expected_disbursement_date || '',
            l.sanctioned_amount || '',
            exec,
          ];
        }),
        'pipeline_mis'
      );
    } catch (e) {
      toast.error('Failed to export pipeline data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {/* Top KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
        <Kpi label="Total Leads in System" value={loading?null:total}
          icon={<FiUsers size={15}/>} iconBg={C.navyL} iconColor={C.navy} accent={C.navy} loading={loading}/>
        <Kpi label="Active (In Pipeline)"  value={loading?null:active}
          icon={<FiActivity size={15}/>} iconBg={C.skyL} iconColor={C.skyB} accent={C.skyB} loading={loading}/>
        <Kpi label="Disbursed"             value={loading?null:disb}
          icon={<FiDollarSign size={15}/>} iconBg={C.greenL} iconColor={C.greenB} accent={C.greenB} loading={loading}
          onClick={() => navigate('/leads?stage=DISBURSEMENT')}/>
        <Kpi label="Closed"                value={loading?null:closed}
          icon={<FiCheckCircle size={15}/>} iconBg='#F1F5F9' iconColor='#64748B' accent='#94A3B8' loading={loading}
          onClick={() => navigate('/leads?stage=CLOSED')}/>
      </div>

      {loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:14 }}>
          </div>
        </div>
      )}
      {!loading && error && <Err msg={error} onRetry={load}/>}

      {!loading && !error && data !== null && (
        <>
          {/* Stage cards grid - all 7 stages in one grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            {stageCards.map(s => (
              <div key={s.label} onClick={() => s.stage && navigate(`/leads?stage=${s.stage}`)} style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
                padding:'16px 18px', boxShadow:'0 2px 6px rgba(13,59,94,0.06)',
                borderTop:`3px solid ${s.color}`, position:'relative', overflow:'hidden',
                cursor: s.stage ? 'pointer' : 'default', transition:'transform 0.15s,box-shadow 0.15s' }}
                onMouseEnter={e => { if(s.stage){e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 16px rgba(13,59,94,0.12)';} }}
                onMouseLeave={e => { if(s.stage){e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='';} }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
                  <div style={{ width:34, height:34, borderRadius:9, background:s.bg,
                    display:'flex', alignItems:'center', justifyContent:'center', color:s.color }}>{s.icon}</div>
                  <span style={{ fontSize:12, fontWeight:700, color:s.color, background:s.bg,
                    padding:'2px 8px', borderRadius:20 }}>{s.pct}%</span>
                </div>
                <div style={{ fontSize:28, fontWeight:800, color:C.t1, letterSpacing:'-0.04em', lineHeight:1, marginBottom:5 }}>{s.value}</div>
                <div style={{ fontSize:12, fontWeight:600, color:C.t3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Pipeline progress bars */}
          <Card title="Pipeline Distribution" icon={<FiBarChart2 size={13}/>}
            headerRight={
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={load}
                  style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`,
                    background:C.white, color:C.t3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <FiRefreshCw size={13}/>
                </button>
                <button onClick={doExport} disabled={exporting}
                  style={{ display:'flex', alignItems:'center', gap:5, height:32, padding:'0 13px',
                    borderRadius:7, background:C.goldB, color:'#0D1B2A', border:'none', fontWeight:700,
                    fontSize:12, cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.7 : 1 }}>
                  {exporting
                    ? <><div style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(13,27,42,0.3)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} /> Exporting…</>
                    : <><FiDownload size={11}/> Export</>}
                </button>
              </div>
            }>
            {stageCards.map(s => (
              <PBar key={s.label} label={s.label} value={s.value} total={total} color={s.color}/>
            ))}
            <div style={{ paddingTop:14, borderTop:`1px solid ${C.borderL}`,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:12, color:C.t3 }}>Total leads across all stages</span>
              <span style={{ fontSize:18, fontWeight:800, color:C.navy }}>{total}</span>
            </div>
          </Card>
        </>
      )}
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   TAB 4 — USER-WISE MIS
   Real API: GET /api/leads/mis/user_wise/?year=YYYY&month=MM
   Response: { "detail": "year and month required" } if missing params
   Once supplied → actual shape handled gracefully.
═════════════════════════════════════════════════════════════════ */
function UserWiseMIS() {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [year,  setYear]      = useState(String(curYear));
  const [month, setMonth]     = useState(String(curMonth));
  const [searched, setSearched] = useState(false);
  const [search, setSearch]   = useState('');

  const load = useCallback(async (y = year, m = month) => {
    if (!y || !m) { setError('Please select both year and month.'); return; }
    setLoading(true); setError(null); setSearched(true);
    try {
      // Use POST to incentive calculate API (same as Task-7)
      const res = await api.calculateIncentive({ year: Number(y), month: Number(m) });
      const d   = res?.data;
      if (d?.detail) throw new Error(d.detail);
      setData(d);
    } catch (e) {
      setError(e?.message || e?.detail || String(e) || 'Failed to load user-wise MIS');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  const rows = Array.isArray(data?.data) ? data.data
           : Array.isArray(data?.results) ? data.results
           : Array.isArray(data) ? data
           : [];

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    const ud = r.user_detail || {};
    const fn = ud.first_name || '';
    const ln = ud.last_name || '';
    const name = [fn, ln].filter(Boolean).join(' ');
    return name.toLowerCase().includes(q) || ud.email?.toLowerCase().includes(q);
  });

  // Fields from incentive calculate API
  const totalDisb   = rows.reduce((s,r) => s + Number(r.total_disbursement||0), 0);
  const totalRev    = rows.reduce((s,r) => s + Number(r.total_net_to_company||0), 0);
  const totalIncent = rows.reduce((s,r) => s + Number(r.incentive_amount||0), 0);

  const YEARS  = Array.from({ length: 5 }, (_, i) => String(curYear - i));
  const MONTHS = [
    { v:'01',l:'January'   }, { v:'02',l:'February'  }, { v:'03',l:'March'     },
    { v:'04',l:'April'     }, { v:'05',l:'May'        }, { v:'06',l:'June'      },
    { v:'07',l:'July'      }, { v:'08',l:'August'     }, { v:'09',l:'September' },
    { v:'10',l:'October'   }, { v:'11',l:'November'   }, { v:'12',l:'December'  },
  ];
  const monthLabel = year && month
    ? new Date(`${year}-${month}-01`).toLocaleString('en-IN', { month:'long', year:'numeric' })
    : '';

  const AVATAR_COLORS = ['#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B'];

  const [caseExporting, setCaseExporting] = useState(false);

  const doExport = () => {
    if (rows.length > 0) {
      exportCsv(
        ['User','Email','Disbursed','BSA Payout','Net to Company','Excess','Incentive'],
        rows.map(r => {
          const ud = r.user_detail || {};
          const fn = ud.first_name || '';
          const ln = ud.last_name || '';
          const name = [fn, ln].filter(Boolean).join(' ');
          return [
            name,
            ud.email || '',
            r.total_disbursement || '0.00',
            r.total_bsa_payout || '0.00',
            r.total_net_to_company || '0.00',
            r.excess_above_cutoff || '0.00',
            r.incentive_amount || '0.00'
          ];
        }),
        `user_wise_mis_${year}_${month}`
      );
    }
  };

  const doCaseExport = async () => {
    setCaseExporting(true);
    try {
      const allLeads = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await api.getLeads({ page, page_size: 100 });
        const d = res?.data;
        const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
        allLeads.push(...results);
        hasMore = !!d?.next && results.length > 0;
        page++;
      }

      const userMap = {};
      rows.forEach(r => {
        const ud = r.user_detail || {};
        const name = [ud.first_name, ud.last_name].filter(Boolean).join(' ') || ud.email || `User #${r.id}`;
        userMap[r.user] = name;
      });

      const stageMap = {
        FOLLOW_UP: 'Follow-up', LOGIN: 'Login', SANCTION: 'Sanction',
        DISBURSEMENT: 'Disbursement', PDD: 'PDD', REVENUE: 'Revenue', CLOSED: 'Closed',
      };

      exportCsv(
        ['User Name','Lead No','Customer','Product','Disb. Date','Disbursed Amount','Payout to Company','BSA Payout','Net to Company','PDD Status'],
        allLeads.map(l => {
          const exec = userMap[l.created_by] || l.created_by_detail?.first_name || '';
          const pddStatus = l.current_stage === 'PDD' ? (l.pdd_status || 'Pending')
            : l.current_stage === 'REVENUE' ? 'Cleared'
            : l.current_stage === 'CLOSED' ? 'N/A' : '—';
          return [
            exec,
            l.lead_no || `#${l.id}`,
            l.customer_name || '',
            l.product?.name || '',
            l.disbursement_date || '',
            l.disbursed_amount || '',
            l.payout_to_company || '',
            l.bsa_payout || '',
            l.net_to_company || '',
            pddStatus,
          ];
        }),
        `user_wise_cases_${year}_${month}`
      );
    } catch (e) {
      toast.error('Failed to export case details');
    } finally {
      setCaseExporting(false);
    }
  };

  return (
    <>
      

      {/* Param selector */}
      <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
        padding:'16px 20px', marginBottom:14, boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
        <div style={{ fontSize:11.5, fontWeight:700, color:C.t3, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>Select Period</div>
        <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Year</label>
            <select value={year} onChange={e => setYear(e.target.value)}
              style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
                fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit', minWidth:100 }}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:'block', marginBottom:5 }}>Month</label>
            <select value={month} onChange={e => setMonth(e.target.value)}
              style={{ height:36, border:`1px solid ${C.border}`, borderRadius:8, padding:'0 10px',
                fontSize:13, color:C.t1, background:C.white, outline:'none', fontFamily:'inherit', minWidth:140 }}>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <button onClick={() => load(year, month)}
            style={{ height:36, padding:'0 20px', borderRadius:8, background:C.navy, color:'#fff',
              border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
              display:'flex', alignItems:'center', gap:6 }}>
            <FiFilter size={12}/> Load Report
          </button>
          {rows.length > 0 && (
            <>
              <button onClick={doExport}
                style={{ height:36, padding:'0 16px', borderRadius:8, background:C.goldB, color:'#0D1B2A',
                  border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                  display:'flex', alignItems:'center', gap:6 }}>
                <FiDownload size={12}/> Export Summary
              </button>
              <button onClick={doCaseExport} disabled={caseExporting}
                style={{ height:36, padding:'0 16px', borderRadius:8, background:C.tealB, color:'#fff',
                  border:'none', fontSize:13, fontWeight:700, cursor: caseExporting ? 'not-allowed' : 'pointer', fontFamily:'inherit',
                  display:'flex', alignItems:'center', gap:6, opacity: caseExporting ? 0.7 : 1 }}>
                {caseExporting
                  ? <><div style={{ width:10, height:10, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} /> Exporting…</>
                  : <><FiDownload size={12}/> Export Case Details</>}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Not yet searched */}
      {!searched && !loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'48px 24px', textAlign:'center', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ width:52, height:52, borderRadius:'50%', background:C.navyL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
            <FiUsers size={22} style={{ color:C.navy, opacity:0.5 }}/>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>Select a period to load User-wise MIS</div>
          <div style={{ fontSize:12.5, color:C.t3 }}>Choose year and month, then click "Load Report"</div>
        </div>
      )}

      {loading && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[0,1,2,3,4].map(i=><Sh key={i} h={52}/>)}
          </div>
        </div>
      )}

      {!loading && error && <Err msg={error} onRetry={() => load(year, month)}/>}

      {!loading && !error && searched && data !== null && (
        <>
          {/* KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            <Kpi label="Executives"    value={rows.length}          icon={<FiUsers size={15}/>}     iconBg={C.navyL}   iconColor={C.navy}  accent={C.navy}   loading={false} sub={monthLabel}/>
            <Kpi label="Total Disbursed" value={fmtAmt(totalDisb)} icon={<FiDollarSign size={15}/>} iconBg={C.greenL}  iconColor={C.greenB} accent={C.greenB} loading={false}/>
            <Kpi label="Total Revenue" value={fmtAmt(totalRev)}    icon={<FiTrendingUp size={15}/>} iconBg={C.tealL}   iconColor={C.tealB} accent={C.tealB}  loading={false}/>
            <Kpi label="Total Incentive" value={fmtAmt(totalIncent)} icon={<FiActivity size={15}/>} iconBg={C.goldL}   iconColor={C.gold}  accent={C.goldB}  loading={false}/>
          </div>

          {/* Table or summary */}
          {rows.length === 0 ? (
            <Card title={`User-wise MIS — ${monthLabel}`} icon={<FiUsers size={13}/>}>
              {/* Check if API returned a non-list aggregate */}
              {data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0 && !data.results ? (
                <div>
                  <div style={{ marginBottom:14, padding:'10px 14px', background:C.skyL, borderRadius:9, fontSize:12.5, color:C.sky, fontWeight:500 }}>
                    API returned an aggregate summary. Detailed per-user breakdown will be available when the backend exposes individual records.
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12 }}>
                    {Object.entries(data).map(([k,v]) => (
                      <div key={k} style={{ background:C.borderL, borderRadius:9, padding:'12px 14px' }}>
                        <div style={{ fontSize:10.5, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>{k.replace(/_/g,' ')}</div>
                        <div style={{ fontSize:16, fontWeight:700, color:C.t1 }}>{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Empty label={`No user data for ${monthLabel}`}/>
              )}
            </Card>
          ) : (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
              overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
              <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC',
                display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <FiUsers size={14} style={{ color:C.navy, opacity:0.7 }}/>
                  <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>User-wise — {monthLabel}</span>
                  <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:999,
                    fontSize:10.5, fontWeight:700, border:`1px solid rgba(13,59,94,0.12)` }}>{filtered.length}</span>
                </div>
                <div style={{ position:'relative' }}>
                  <FiSearch size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
                  <input type="text" placeholder="Search user…" value={search} onChange={e=>setSearch(e.target.value)}
                    style={{ height:32, border:`1px solid ${C.border}`, borderRadius:7, padding:'0 10px 0 28px',
                      fontSize:12.5, color:C.t1, background:C.bg, outline:'none', fontFamily:'inherit', width:180 }}
                    onFocus={e=>e.target.style.borderColor=C.navy} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {['#','Executive','Email','Disbursed','BSA Payout','Net to Co.','Excess','Incentive ₹'].map((h,i)=>(
                        <th key={h} style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t2,
                          textTransform:'uppercase', letterSpacing:'0.07em',
                          textAlign:i>=3?'right':'left',
                          borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, idx) => {
                      const isLast  = idx === filtered.length - 1;
                      const ud      = r.user_detail || {};
                      const fn      = ud.first_name || '';
                      const ln      = ud.last_name  || '';
                      const name    = [fn, ln].filter(Boolean).join(' ') || ud.email || `User #${r.id}` || '—';
                      const email   = ud.email || '';
                      const disbAmt = r.total_disbursement || '0.00';
                      const bsaPay  = r.total_bsa_payout || '0.00';
                      const netCo   = r.total_net_to_company || '0.00';
                      const excess  = r.excess_above_cutoff || '0.00';
                      const incent  = r.incentive_amount || '0.00';
                      const aColor  = AVATAR_COLORS[(name.charCodeAt(0)||0) % AVATAR_COLORS.length];
                      return (
                        <tr key={r.id||idx}>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t4 }}>{idx+1}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                              <div style={{ width:32, height:32, borderRadius:'50%', background:aColor+'20', border:`1.5px solid ${aColor}40`,
                                color:aColor, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:11, flexShrink:0 }}>
                                {name.split(' ').map(p=>p[0]).slice(0,2).join('').toUpperCase()||'?'}
                              </div>
                              <div>
                                <div style={{ fontWeight:700, fontSize:13, color:C.t1 }}>{name}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, color:C.t2 }}>{email || '—'}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:800, color:C.greenB }}>{fmtAmt(disbAmt)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, color:C.t2 }}>{fmtAmt(bsaPay)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, fontWeight:700, color:C.navy }}>{fmtAmt(netCo)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <span style={{ fontSize:13, color: Number(excess) > 0 ? C.greenB : C.t4 }}>{Number(excess) > 0 ? fmtAmt(excess) : '—'}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            {Number(incent) > 0
                              ? <span style={{ fontSize:13, fontWeight:800, color:C.gold, background:C.goldL, padding:'3px 10px', borderRadius:20, border:'1px solid #FDE68A' }}>{fmtAmt(incent)}</span>
                              : <span style={{ fontSize:12.5, color:C.t4 }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ padding:'9px 18px', borderTop:`1px solid ${C.borderL}`, fontSize:11.5, color:C.t4, background:'#FAFBFC' }}>
                Showing {filtered.length} of {rows.length} executives · {monthLabel}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   MAIN REPORTS PAGE — 4 TABS
═════════════════════════════════════════════════════════════════ */
const TABS = [
  { id:'last_month', label:'Last Month MIS',  icon:<FiCalendar size={13}/>,   color:'#0369A1', bg:C.skyL,    activeBg:'#0369A1', shadow:'rgba(3,105,161,0.28)'   },
  { id:'overall',    label:'Overall MIS',     icon:<FiBarChart2 size={13}/>,  color:C.navy,    bg:C.navyL,   activeBg:C.navy,    shadow:'rgba(13,59,94,0.28)'    },
  { id:'pipeline',   label:'Pipeline MIS',    icon:<FiTrendingUp size={13}/>, color:C.green,   bg:C.greenL,  activeBg:'#15803D', shadow:'rgba(21,128,61,0.28)'   },
  { id:'user_wise',  label:'User-wise MIS',   icon:<FiUsers size={13}/>,      color:C.purple,  bg:C.purpleL, activeBg:'#6D28D9', shadow:'rgba(109,40,217,0.28)'  },
];

export default function Reports() {
  const [activeTab, setActiveTab] = useState('pipeline'); // pipeline works without extra params

  return (
    <>
      <style>{`
        @keyframes rptShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes rptFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .rpt-anim { animation: rptFadeUp 0.22s ease both; }
      `}</style>

      <Layout pageTitle="Reports">
        <div className="rpt-anim" style={{ display:'flex', flexDirection:'column', gap:14,
          fontFamily:"'Inter',sans-serif", paddingBottom:28 }}>

          {/* Hero */}
          <div style={{ borderRadius:14, overflow:'hidden',
            background:'linear-gradient(120deg,#0D3B5E,#1A5276)',
            boxShadow:'0 4px 20px rgba(0,0,0,0.18)', position:'relative' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
              background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ padding:'20px 28px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.48)',
                marginBottom:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>Reports</div>
              <h2 style={{ margin:0, color:'#fff', fontSize:20, fontWeight:800,
                letterSpacing:'-0.03em', lineHeight:1.2 }}>MIS Reports</h2>
              <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>
                Monthly Information System — Lead pipeline, disbursements, revenue & incentive tracking
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {TABS.map(t => {
              const isAct = activeTab === t.id;
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px',
                    border:'none', borderRadius:10, cursor:'pointer', fontFamily:'inherit',
                    fontSize:13, transition:'all 0.15s',
                    background: isAct ? t.activeBg : t.bg,
                    color:      isAct ? '#fff' : t.color,
                    fontWeight: isAct ? 700 : 600,
                    boxShadow:  isAct ? `0 3px 10px ${t.shadow}` : 'none',
                    transform:  isAct ? 'translateY(-1px)' : 'none' }}
                  onMouseEnter={e=>{ if(!isAct){ e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow=`0 3px 10px ${t.shadow}`; }}}
                  onMouseLeave={e=>{ if(!isAct){ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='none'; }}}>
                  {t.icon} {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div key={activeTab}>
            {activeTab === 'last_month' && <LastMonthMIS/>}
            {activeTab === 'overall'    && <OverallMIS/>}
            {activeTab === 'pipeline'   && <PipelineMIS/>}
            {activeTab === 'user_wise'  && <UserWiseMIS/>}
          </div>

        </div>
      </Layout>
    </>
  );
}